import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, Navigate, useNavigate, useParams } from '@tanstack/react-router'
import { Bot, Copy, Fingerprint, MessageCircle } from 'lucide-react'
import { useEffect, useState, type FormEvent } from 'react'
import { toast } from 'sonner'
import { ApiError, api, del, get, patch, post } from '../lib/api.ts'
import { fmtDate, fmtDateTime } from '../lib/format.ts'
import { useMe } from '../lib/hooks.ts'
import { loginWithPasskey, passkeysSupported, registerPasskey } from '../lib/passkeys.ts'
import { currentSubscription, disablePush, enablePush, fetchPushState, isIos, isStandalone, pushSupported } from '../lib/push.ts'
import { Badge, Button, ErrorNote, Field, Input, PageHeader, Select, Sheet, Spinner } from '../components/ui.tsx'
import { t } from '../lib/i18n.ts'
import { LanguageToggle } from '../components/LanguageToggle.tsx'
import { MyCondos } from './my-condos.tsx'

function AuthFrame({ children, aside }: { children: React.ReactNode; aside?: React.ReactNode }) {
  return (
    <main className="min-h-dvh md:grid md:grid-cols-[1fr_1.1fr]">
      <section className="almaco hidden border-r border-pauta md:block">
        <div className="max-w-md py-16 pr-10 pl-16">
          <p className="text-[1.05rem] font-semibold text-caneta">{t('Subsíndico')}</p>
          <h1 className="mt-6 font-doc text-[2.6rem] leading-[1.12] font-semibold">
            {t('A convenção, o regimento e as atas do seu prédio, respondendo por você.')}
          </h1>
          <p className="mt-6 max-w-sm text-[1.05rem] text-tinta-2">
            {t('Pergunte em português e receba a resposta com o artigo e a página de onde ela veio.')}
          </p>
          {aside}
        </div>
      </section>
      <section className="flex items-center justify-center px-5 py-12">
        <div className="w-full max-w-sm">
          <p className="mb-6 text-[1.05rem] font-semibold text-caneta md:hidden">{t('Subsíndico')}</p>
          {children}
          <p className="mt-10 flex flex-wrap items-center gap-4 text-sm text-tinta-2"><a className="underline" href="/privacidade">{t('Privacidade')}</a><a className="underline" href="/termos">{t('Termos de uso')}</a><LanguageToggle signedIn={false} className="ml-auto" /></p>
        </div>
      </section>
    </main>
  )
}

function PhoneCodeForm({ inviteToken, needName, nameHint, onDone }: { inviteToken?: string; needName?: boolean; nameHint?: string | null; onDone: (r: { condoSlug: string | null }) => void }) {
  const [phone, setPhone] = useState('')
  const [code, setCode] = useState('')
  const [name, setName] = useState(nameHint ?? '')
  const [step, setStep] = useState<'phone' | 'code'>('phone')
  const send = useMutation({
    mutationFn: () => post('/api/auth/otp', { phone, inviteToken }),
    onSuccess: () => setStep('code'),
  })
  const verify = useMutation({
    mutationFn: () => post<{ condoSlug: string | null }>('/api/auth/verify', { phone, code, inviteToken, ...(needName && name ? { name } : {}) }),
    onSuccess: onDone,
  })
  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (step === 'phone') send.mutate()
    else verify.mutate()
  }
  return (
    <form onSubmit={submit} className="flex flex-col gap-4">
      <Field label={t('Seu WhatsApp')} htmlFor="phone" hint={t('Com DDD. Enviamos um código de 6 dígitos.')}>
        <Input id="phone" type="tel" inputMode="tel" autoComplete="tel" placeholder="(31) 99999-0000" value={phone} onChange={(e) => setPhone(e.target.value)} disabled={step === 'code'} required />
      </Field>
      {step === 'code' && (
        <>
          <p className="flex items-start gap-2 rounded-ficha bg-carimbo-claro px-3 py-2 text-[0.95rem] text-carimbo">
            <MessageCircle className="mt-0.5 size-4 shrink-0" />{' '}{t('Se este número tiver acesso, o código chegou no WhatsApp.')}
          </p>
          <Field label={t('Código')} htmlFor="code">
            <Input id="code" inputMode="numeric" autoComplete="one-time-code" pattern="\d{6}" maxLength={6} placeholder="000000" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} autoFocus required className="text-center text-2xl tracking-[0.35em]" />
          </Field>
          {needName && (
            <Field label={t('Seu nome')} htmlFor="name" hint={t('Como os vizinhos e o síndico vão ver você.')}>
              <Input id="name" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} required maxLength={120} />
            </Field>
          )}
        </>
      )}
      <ErrorNote error={send.error ?? verify.error} />
      <Button type="submit" busy={send.isPending || verify.isPending}>
        {step === 'phone' ? t('Receber código') : t('Entrar')}
      </Button>
      {step === 'code' && (
        <button type="button" className="text-[0.95rem] font-semibold text-caneta" onClick={() => { setStep('phone'); setCode('') }}>
          {t('Usar outro número')}
        </button>
      )}
    </form>
  )
}

export function Login() {
  const qc = useQueryClient()
  const nav = useNavigate()
  const err = new URLSearchParams(location.search).get('erro')
  const done = async () => {
    await qc.invalidateQueries({ queryKey: ['me'] })
    const next = new URLSearchParams(location.search).get('next')
    nav({ to: next && next.startsWith('/') && !next.startsWith('//') ? next : '/' })
  }
  const passkey = useMutation({ mutationFn: loginWithPasskey, onSuccess: done })
  const options = useQuery({ queryKey: ['auth-options'], queryFn: () => get<{ reviewAccess: boolean }>('/api/auth/options'), staleTime: 300_000 })
  return (
    <AuthFrame>
      <h1 className="text-[1.8rem] font-bold">{t('Entrar')}</h1>
      <p className="mt-1 mb-6 text-tinta-2">{t('O acesso é por convite do síndico. Use o número de WhatsApp do convite.')}</p>
      {err === 'link' && <div className="mb-4"><ErrorNote error={new Error(t('Link de acesso inválido ou expirado.'))} /></div>}
      <PhoneCodeForm onDone={done} />
      {passkeysSupported() && (
        <div className="mt-6 border-t border-pauta pt-4">
          <Button type="button" variant="outline" className="w-full" busy={passkey.isPending} onClick={() => passkey.mutate()}><Fingerprint className="size-5" />{' '}{t('Entrar com chave de acesso')}</Button>
          <p className="mt-2 text-sm text-tinta-2">{t('Biometria ou PIN do aparelho, se você já cadastrou uma chave em Meu perfil.')}</p>
          {passkey.error && !(passkey.error instanceof DOMException && passkey.error.name === 'NotAllowedError') && <div className="mt-2"><ErrorNote error={passkey.error} /></div>}
        </div>
      )}
      {options.data?.reviewAccess && <ReviewLogin onDone={done} />}
    </AuthFrame>
  )
}

/** Reviewer access (ADR 0039): a password the operator issued for a demo account; offered only when the server has it on. */
function ReviewLogin({ onDone }: { onDone: () => void }) {
  const [phone, setPhone] = useState('')
  const [password, setPassword] = useState('')
  const login = useMutation({ mutationFn: () => post('/api/auth/review', { phone, password }), onSuccess: onDone })
  return (
    <details className="mt-6 border-t border-pauta pt-4 text-[0.95rem]">
      <summary className="cursor-pointer font-semibold text-caneta">{t('Acesso de avaliação')}</summary>
      <p className="mt-2 text-sm text-tinta-2">{t('Para quem recebeu da BitOps uma senha de avaliação de um condomínio de demonstração.')}</p>
      <form className="mt-3 flex flex-col gap-3" onSubmit={(e) => { e.preventDefault(); login.mutate() }}>
        <Field label={t('Telefone')} htmlFor="rv-phone"><Input id="rv-phone" type="tel" autoComplete="username" value={phone} onChange={(e) => setPhone(e.target.value)} required /></Field>
        <Field label={t('Senha de avaliação')} htmlFor="rv-pass"><Input id="rv-pass" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required /></Field>
        <ErrorNote error={login.error} />
        <Button type="submit" variant="outline" busy={login.isPending}>{t('Entrar para avaliar')}</Button>
      </form>
    </details>
  )
}

interface InvitePreview { condoName: string; roleLabel: string; unitLabel: string | null; nameHint: string | null; phoneBound: boolean }

export function InvitePage() {
  const { token } = useParams({ strict: false }) as { token: string }
  const me = useMe()
  const qc = useQueryClient()
  const nav = useNavigate()
  const inv = useQuery({ queryKey: ['invite', token], queryFn: () => get<InvitePreview>(`/api/invites/${token}`), retry: false })
  const accept = useMutation({
    mutationFn: () => post<{ condoSlug: string }>(`/api/invites/${token}/accept`),
    onSuccess: async (r) => {
      await qc.invalidateQueries({ queryKey: ['me'] })
      nav({ to: '/c/$slug', params: { slug: r.condoSlug } })
    },
  })
  if (inv.isLoading || me.isLoading) return <AuthFrame><Spinner /></AuthFrame>
  if (inv.error) {
    return (
      <AuthFrame>
        <h1 className="text-[1.8rem] font-bold">{t('Convite indisponível')}</h1>
        <p className="mt-2 text-tinta-2">{(inv.error as Error).message}{' '}{t('Peça um novo convite ao síndico.')}</p>
        <Link to="/entrar" className="mt-6 inline-block font-semibold text-caneta underline">{t('Já tenho acesso')}</Link>
      </AuthFrame>
    )
  }
  const i = inv.data!
  return (
    <AuthFrame>
      <h1 className="text-[1.8rem] font-bold leading-tight">{t('Você foi convidado para o')}{' '}{i.condoName}</h1>
      <p className="mt-2 mb-6 flex flex-wrap items-center gap-2 text-tinta-2">
        <Badge tone="caneta">{t(i.roleLabel)}</Badge>
        {i.unitLabel && <Badge>{t('Unidade')}{' '}{i.unitLabel}</Badge>}
      </p>
      {me.data?.user ? (
        <div className="flex flex-col gap-3">
          <p>{t('Entrou como')}{' '}<strong>{me.data.user.name}</strong>.</p>
          <ErrorNote error={accept.error} />
          <Button onClick={() => accept.mutate()} busy={accept.isPending}>{t('Aceitar convite')}</Button>
        </div>
      ) : (
        <PhoneCodeForm
          inviteToken={token}
          needName
          nameHint={i.nameHint}
          onDone={async (r) => {
            await qc.invalidateQueries({ queryKey: ['me'] })
            nav(r.condoSlug ? { to: '/c/$slug', params: { slug: r.condoSlug } } : { to: '/' })
          }}
        />
      )}
    </AuthFrame>
  )
}

export function Home() {
  const me = useMe()
  if (me.isLoading) return <div className="p-6"><Spinner /></div>
  if (!me.data?.user) return <Navigate to="/entrar" />
  const ms = me.data.memberships
  if (ms.length === 1) return <Navigate to="/c/$slug" params={{ slug: ms[0]!.slug }} />
  return (
    <main className="mx-auto max-w-xl px-5 py-12">
      <p className="font-semibold text-caneta">{t('Subsíndico')}</p>
      <h1 className="mt-2 text-[1.8rem] font-bold">{t('Olá,')}{' '}{me.data.user.name.split(' ')[0]}</h1>
      {ms.length === 0 ? (
        <p className="mt-3 text-tinta-2">{t('Você ainda não participa de nenhum condomínio. Peça um convite ao síndico.')}</p>
      ) : (
        <MyCondos memberships={ms} />
      )}
      <div className="mt-8 flex gap-4 text-[0.95rem]">
        <Link to="/perfil" className="font-semibold text-caneta">{t('Meu perfil')}</Link>
        {me.data.user.isPlatformAdmin && <Link to="/plataforma" className="font-semibold text-caneta">{t('Plataforma')}</Link>}
      </div>
    </main>
  )
}

/** Web push on this device (ADR 0037): the checkbox mirrors the browser's own subscription; permission is asked only on the tap. */
function PushSection({ available }: { available: boolean }) {
  const q = useQuery({ queryKey: ['push-state'], queryFn: fetchPushState, enabled: available })
  const qc = useQueryClient()
  const [onDevice, setOnDevice] = useState<boolean | null>(null)
  useEffect(() => {
    currentSubscription().then((s) => setOnDevice(Boolean(s))).catch(() => setOnDevice(false))
  }, [])
  const done = (on: boolean) => () => { setOnDevice(on); qc.invalidateQueries({ queryKey: ['push-state'] }); toast.success(on ? t('Avisos ativados neste aparelho') : t('Avisos desativados neste aparelho')) }
  const on = useMutation({ mutationFn: () => enablePush(q.data!.publicKey!), onSuccess: done(true), onError: (e) => toast.error((e as Error).message) })
  const off = useMutation({ mutationFn: disablePush, onSuccess: done(false), onError: (e) => toast.error((e as Error).message) })
  if (!pushSupported()) {
    return (
      <p className="mt-1 text-tinta-2">
        {t('Este navegador não recebe avisos.')}{isIos() && !isStandalone() ? t(' No iPhone (iOS 16.4 ou mais novo), toque em Compartilhar → Adicionar à Tela de Início, abra o Subsíndico por lá e ative os avisos aqui.') : ''}
      </p>
    )
  }
  if (!available) return <p className="mt-1 text-tinta-2">{t('O serviço de avisos no celular ainda não foi configurado neste servidor. Os avisos continuam chegando pelo WhatsApp e por e-mail.')}</p>
  return (
    <>
      <p className="mt-1 text-tinta-2">{t('Os mesmos avisos do WhatsApp, como notificação neste aparelho. A permissão do navegador é pedida só quando você ativar.')}</p>
      <label className="mt-3 flex items-start gap-3">
        <input type="checkbox" className="mt-1 size-5 accent-caneta" checked={onDevice === true} disabled={onDevice === null || !q.data?.publicKey || on.isPending || off.isPending} onChange={(e) => (e.target.checked ? on.mutate() : off.mutate())} />
        <span>
          {t('Receber avisos neste aparelho')}
          <span className="block text-sm text-tinta-2">{q.data && q.data.subscriptions > 0 ? q.data.subscriptions === 1 ? t('1 aparelho com avisos ativos.') : t('{n} aparelhos com avisos ativos.', { n: q.data.subscriptions }) : t('Nenhum aparelho com avisos ativos.')}</span>
        </span>
      </label>
    </>
  )
}

export function Profile() {
  const me = useMe()
  const qc = useQueryClient()
  const [name, setName] = useState<string | null>(null)
  const save = useMutation({
    mutationFn: (b: { name?: string; whatsappOptIn?: boolean; whatsappAssistant?: boolean; emailOptIn?: boolean }) => patch('/api/me', b),
    onSuccess: () => {
      toast.success(t('Perfil salvo'))
      qc.invalidateQueries({ queryKey: ['me'] })
    },
  })
  const all = useMutation({ mutationFn: () => post('/api/auth/logout-all'), onSuccess: () => (location.href = '/entrar') })
  const [email, setEmail] = useState('')
  const reqEmail = useMutation({ mutationFn: (addr: string) => post('/api/me/email', { email: addr }), onSuccess: () => { setEmail(''); qc.invalidateQueries({ queryKey: ['me'] }) } })
  const dropEmail = useMutation({ mutationFn: () => del('/api/me/email'), onSuccess: () => { toast.success(t('E-mail removido')); qc.invalidateQueries({ queryKey: ['me'] }) } })
  const emailFlag = new URLSearchParams(location.search).get('email')
  if (me.isLoading) return <Spinner />
  if (!me.data?.user) return <Navigate to="/entrar" />
  const u = me.data.user
  return (
    <main className="mx-auto max-w-xl px-5 py-10">
      <Link to="/" className="text-[0.95rem] font-semibold text-caneta">{t('Voltar')}</Link>
      <PageHeader title={t('Meu perfil')} />
      <Sheet className="p-5">
        <form className="flex flex-col gap-4" onSubmit={(e) => { e.preventDefault(); if (name) save.mutate({ name }) }}>
          <Field label={t('Nome')} htmlFor="pname">
            <Input id="pname" value={name ?? u.name} onChange={(e) => setName(e.target.value)} maxLength={120} />
          </Field>
          <p className="text-[0.95rem] text-tinta-2">{t('WhatsApp:')}{' '}{u.phone}</p>
          <label className="flex items-start gap-3">
            <input type="checkbox" className="mt-1 size-5 accent-caneta" checked={u.whatsappOptIn} onChange={(e) => save.mutate({ whatsappOptIn: e.target.checked })} />
            <span>{t('Receber avisos do condomínio pelo WhatsApp')}{' '}<span className="block text-sm text-tinta-2">{t('Comunicados, lembretes de reserva e assembleias. Os códigos de acesso chegam sempre.')}</span></span>
          </label>
          {u.whatsappAssistantEnabled && (
            <label className="flex items-start gap-3">
              <input type="checkbox" className="mt-1 size-5 accent-caneta" checked={u.whatsappAssistant} onChange={(e) => save.mutate({ whatsappAssistant: e.target.checked })} />
              <span>{t('Perguntar ao Subsíndico pelo WhatsApp')}{' '}<span className="block text-sm text-tinta-2">{t('Mande a pergunta para o mesmo número que envia os códigos (“até que horas pode ter barulho?”) e receba a resposta com a fonte. Só responde a quem ativar aqui; conversas em grupo são ignoradas.')}</span></span>
            </label>
          )}
          <ErrorNote error={save.error} />
          <div><Button type="submit" busy={save.isPending} disabled={!name || name === u.name}>{t('Salvar nome')}</Button></div>
        </form>
      </Sheet>
      {u.emailChannel && (
        <Sheet className="mt-5 p-5">
          <h2 className="font-bold">{t('E-mail')}</h2>
          <p className="mt-1 text-tinta-2">{t('Um segundo canal para os mesmos avisos do WhatsApp. Confirme o endereço pelo link que enviamos; os códigos de acesso continuam chegando só pelo WhatsApp.')}</p>
          {emailFlag === 'ok' && <p className="mt-2 text-sm font-semibold text-carimbo">{t('E-mail confirmado.')}</p>}
          {emailFlag === 'invalido' && <p className="mt-2 text-sm font-semibold text-margem">{t('Link inválido ou vencido. Peça outro abaixo.')}</p>}
          {u.emailBounced && <p className="mt-2 text-sm font-semibold text-margem" data-testid="email-bounced">{t('As mensagens para o seu e-mail voltaram: o endereço não existe ou recusou o envio. Os avisos por e-mail foram desligados; confirme o endereço de novo ou cadastre outro.')}</p>}
          {u.email && <p className="mt-2 text-[0.95rem]">{t('Confirmado:')}{' '}<span className="font-semibold">{u.email}</span></p>}
          {u.emailPending && <p className="mt-2 text-[0.95rem] text-tinta-2">{t('Aguardando confirmação:')}{' '}{u.emailPending}{t('. Abra o link que enviamos (vale 24 horas).')}</p>}
          <form className="mt-3 flex flex-wrap items-end gap-2" onSubmit={(e) => { e.preventDefault(); reqEmail.mutate(email) }}>
            <Field label={u.email ? t('Trocar o e-mail') : t('Seu e-mail')} htmlFor="pemail"><Input id="pemail" type="email" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={254} autoComplete="email" /></Field>
            <Button type="submit" variant="outline" busy={reqEmail.isPending} disabled={!email.includes('@')}>{t('Enviar link de confirmação')}</Button>
          </form>
          <ErrorNote error={reqEmail.error} />
          {reqEmail.isSuccess && <p className="mt-2 text-sm font-semibold text-carimbo">{t('Enviamos o link. Confirme por lá.')}</p>}
          <label className="mt-3 flex items-start gap-3">
            <input type="checkbox" className="mt-1 size-5 accent-caneta" checked={u.emailOptIn} disabled={!u.emailVerified} onChange={(e) => save.mutate({ emailOptIn: e.target.checked })} />
            <span>{t('Receber avisos também por e-mail')}{' '}<span className="block text-sm text-tinta-2">{u.emailVerified ? t('Os mesmos avisos do WhatsApp, no seu e-mail.') : t('Disponível depois de confirmar o endereço.')}</span></span>
          </label>
          {u.email && <div className="mt-3"><Button type="button" variant="quiet" busy={dropEmail.isPending} onClick={() => dropEmail.mutate()}>{t('Remover e-mail')}</Button></div>}
        </Sheet>
      )}
      <Sheet className="mt-5 p-5">
        <h2 className="font-bold">{t('Idioma')}</h2>
        <p className="mt-1 text-tinta-2">{t('O aplicativo em português ou em inglês. Mensagens no WhatsApp, e-mails, PDFs e as respostas do assistente continuam em português.')}</p>
        <LanguageToggle signedIn className="mt-3" />
      </Sheet>
      <Sheet className="mt-5 p-5">
        <h2 className="font-bold">{t('Avisos no celular')}</h2>
        <PushSection available={u.pushChannel} />
      </Sheet>
      <Sheet className="mt-5 p-5">
        <h2 className="font-bold">{t('Sessões')}</h2>
        <p className="mt-1 text-tinta-2">{t('Perdeu o celular ou entrou em um computador emprestado? Encerre o acesso em todos os aparelhos.')}</p>
        <Button variant="danger" className="mt-4" busy={all.isPending} onClick={() => all.mutate()}>{t('Sair de todos os aparelhos')}</Button>
      </Sheet>
      {all.error instanceof ApiError && <ErrorNote error={all.error} />}
      <Passkeys />
      <AccessTokens />
      <DeleteAccount />
    </main>
  )
}

interface Token { id: string; name: string; hint: string; createdAt: string; lastUsedAt: string | null; expiresAt: string; revokedAt: string | null; status: 'ativo' | 'expirado' | 'revogado' }

interface Passkey { id: string; name: string; deviceType: 'singleDevice' | 'multiDevice'; backedUp: boolean; createdAt: string; lastUsedAt: string | null }

/** Passkeys (ADR 0019): register this device's biometrics or PIN as a way in; remove lost devices. */
function Passkeys() {
  const qc = useQueryClient()
  const [name, setName] = useState(t('Este aparelho'))
  const list = useQuery({ queryKey: ['passkeys'], queryFn: () => get<{ passkeys: Passkey[] }>('/api/me/passkeys') })
  const add = useMutation({
    mutationFn: () => registerPasskey(name.trim() || 'Este aparelho'),
    onSuccess: () => { toast.success(t('Chave de acesso cadastrada')); qc.invalidateQueries({ queryKey: ['passkeys'] }) },
  })
  const remove = useMutation({ mutationFn: (id: string) => del(`/api/me/passkeys/${id}`), onSuccess: () => { toast.success(t('Chave removida')); qc.invalidateQueries({ queryKey: ['passkeys'] }) } })
  const cancelled = add.error instanceof DOMException && add.error.name === 'NotAllowedError'
  return (
    <Sheet className="mt-5 p-5">
      <h2 className="flex items-center gap-2 font-bold"><Fingerprint className="size-5 text-caneta" />{' '}{t('Chaves de acesso (passkeys)')}</h2>
      <p className="mt-1 text-tinta-2">{t('Entre com a biometria ou o PIN deste aparelho, sem esperar o código do WhatsApp. Cadastre uma chave em cada aparelho que usa; remova as dos que perdeu.')}</p>
      {list.isLoading ? <Spinner /> : (list.data?.passkeys.length ?? 0) > 0 && (
        <ul className="mt-3 divide-y divide-pauta rounded-ficha border border-pauta">
          {list.data!.passkeys.map((p) => (
            <li key={p.id} className="flex flex-wrap items-center gap-3 px-3 py-2">
              <span className="min-w-0 flex-1"><span className="font-semibold">{p.name}</span> <span className="block text-sm text-tinta-2">{t('Cadastrada')}{' '}{fmtDate(p.createdAt)}{p.lastUsedAt ? t(', usada {when}', { when: fmtDateTime(p.lastUsedAt) }) : ''}{p.backedUp ? t(', sincronizada na nuvem') : ''}</span></span>
              <Button size="sm" variant="quiet" onClick={() => confirm(t('Remover a chave "{name}"?', { name: p.name })) && remove.mutate(p.id)}>{t('Remover')}</Button>
            </li>
          ))}
        </ul>
      )}
      {passkeysSupported() ? (
        <form className="mt-4 flex flex-wrap items-end gap-2" onSubmit={(e) => { e.preventDefault(); add.mutate() }}>
          <Field label={t('Nome do aparelho')} htmlFor="pk-name"><Input id="pk-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} /></Field>
          <Button type="submit" busy={add.isPending}>{t('Adicionar chave neste aparelho')}</Button>
        </form>
      ) : <p className="mt-3 text-sm text-tinta-2">{t('Este navegador não oferece chaves de acesso.')}</p>}
      {add.error && !cancelled && <div className="mt-2"><ErrorNote error={add.error} /></div>}
      {remove.error && <div className="mt-2"><ErrorNote error={remove.error} /></div>}
    </Sheet>
  )
}

/** Personal access tokens for MCP clients: shown once, revocable, expiring. */
function AccessTokens() {
  const qc = useQueryClient()
  const list = useQuery({ queryKey: ['tokens'], queryFn: () => get<{ tokens: Token[] }>('/api/me/tokens') })
  const [name, setName] = useState('')
  const [days, setDays] = useState('90')
  const [created, setCreated] = useState<{ token: string; expiresAt: string } | null>(null)
  const create = useMutation({
    mutationFn: () => post<{ token: string; expiresAt: string }>('/api/me/tokens', { name, days: Number(days) }),
    onSuccess: (r) => { setCreated(r); setName(''); qc.invalidateQueries({ queryKey: ['tokens'] }) },
  })
  const revoke = useMutation({ mutationFn: (id: string) => del(`/api/me/tokens/${id}`), onSuccess: () => { toast.success(t('Token revogado')); qc.invalidateQueries({ queryKey: ['tokens'] }) } })
  const mcpUrl = `${location.origin}/mcp`
  const copy = (text: string, msg: string) => { void navigator.clipboard.writeText(text); toast.success(msg) }
  const claudeCmd = created ? `claude mcp add --transport http subsindico-ia ${mcpUrl} --header "Authorization: Bearer ${created.token}"` : ''
  const jsonCfg = created ? JSON.stringify({ mcpServers: { 'subsindico-ia': { type: 'http', url: mcpUrl, headers: { Authorization: `Bearer ${created.token}` } } } }, null, 2) : ''
  const active = list.data?.tokens.filter((tk) => tk.status === 'ativo') ?? []
  const old = list.data?.tokens.filter((tk) => tk.status !== 'ativo') ?? []
  return (
    <Sheet className="mt-5 p-5" id="mcp">
      <h2 className="flex items-center gap-2 font-bold"><Bot className="size-5 text-caneta" />{' '}{t('Assistentes de IA (MCP)')}</h2>
      <p className="mt-1 max-w-prose text-tinta-2">
        {t('Conecte o Claude, o ChatGPT, o Claude Code ou o Codex aos dados dos seus condomínios: convenção, regimento, atas, avisos, reservas, prazos. O assistente só vê o que você vê. O jeito mais fácil é colar o endereço no seu assistente e entrar por aqui: veja o passo a passo em')}{' '}
        <Link to="/assistentes" className="font-semibold text-caneta">{t('Conectar assistentes')}</Link>{t('. Os tokens abaixo servem para clientes que não fazem esse login.')}
      </p>
      {created ? (
        <div className="mt-4 flex flex-col gap-3">
          <p className="font-semibold">{t('Token criado. Copie agora: ele não aparece de novo.')}</p>
          <p className="break-all rounded-ficha border border-pauta bg-papel px-3 py-2 font-doc text-[0.95rem]">{created.token}</p>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => copy(created.token, t('Token copiado'))}><Copy className="size-5" />{' '}{t('Copiar token')}</Button>
            <Button variant="outline" onClick={() => copy(claudeCmd, t('Comando copiado'))}><Copy className="size-5" />{' '}{t('Comando do Claude Code')}</Button>
            <Button variant="outline" onClick={() => copy(jsonCfg, t('Configuração copiada'))}><Copy className="size-5" />{' '}{t('JSON (Claude Desktop, Cursor)')}</Button>
          </div>
          <details className="text-[0.95rem]">
            <summary className="cursor-pointer font-semibold text-caneta">{t('Como configurar')}</summary>
            <p className="mt-2"><strong>{t('Claude Code:')}</strong>{' '}{t('cole o comando no terminal.')}</p>
            <pre tabIndex={0} className="mt-1 overflow-x-auto rounded-ficha border border-pauta bg-papel p-3 text-[0.85rem]">{claudeCmd}</pre>
            <p className="mt-2"><strong>{t('Claude Desktop, Cursor e outros:')}</strong>{' '}{t('adicione ao arquivo de configuração de servidores MCP.')}</p>
            <pre tabIndex={0} className="mt-1 overflow-x-auto rounded-ficha border border-pauta bg-papel p-3 text-[0.85rem]">{jsonCfg}</pre>
            <p className="mt-2 text-tinta-2">{t('Vale até')}{' '}{fmtDate(created.expiresAt)}{t('. Depois peça ao assistente: “quais são as regras do salão de festas?”.')}</p>
          </details>
          <div><Button variant="quiet" onClick={() => setCreated(null)}>{t('Concluir')}</Button></div>
        </div>
      ) : (
        <form className="mt-4 flex flex-wrap items-end gap-2" onSubmit={(e) => { e.preventDefault(); create.mutate() }}>
          <Field label={t('Nome do token')} htmlFor="tk-n" hint={t('Ex.: Claude no notebook')}><Input id="tk-n" value={name} onChange={(e) => setName(e.target.value)} required maxLength={80} /></Field>
          <Field label={t('Validade')} htmlFor="tk-d">
            <Select id="tk-d" value={days} onChange={(e) => setDays(e.target.value)} className="w-auto"><option value="30">{t('30 dias')}</option><option value="90">{t('90 dias')}</option><option value="180">{t('180 dias')}</option><option value="365">{t('1 ano')}</option></Select>
          </Field>
          <Button type="submit" busy={create.isPending}>{t('Criar token')}</Button>
          <div className="basis-full"><ErrorNote error={create.error} /></div>
        </form>
      )}
      {list.data && list.data.tokens.length > 0 && (
        <ul className="mt-4">
          {[...active, ...old].map((tk) => (
            <li key={tk.id} className="flex flex-wrap items-center gap-3 border-t border-pauta py-2 text-[0.95rem]">
              <span className="min-w-0 flex-1"><span className="font-semibold">{tk.name}</span> <span className="font-doc text-tinta-2">{tk.hint}</span>
                <span className="block text-sm text-tinta-2">{tk.lastUsedAt ? t('Usado {when}', { when: fmtDateTime(tk.lastUsedAt) }) : t('Nunca usado')}, {tk.status === 'ativo' ? t('vale até {date}', { date: fmtDate(tk.expiresAt) }) : tk.status}</span></span>
              <Badge tone={tk.status === 'ativo' ? 'carimbo' : 'neutral'}>{tk.status}</Badge>
              {tk.status === 'ativo' && <Button size="sm" variant="danger" busy={revoke.isPending && revoke.variables === tk.id} onClick={() => confirm(t('Revogar o token “{name}”? O assistente que o usa perde o acesso agora.', { name: tk.name })) && revoke.mutate(tk.id)}>{t('Revogar')}</Button>}
            </li>
          ))}
        </ul>
      )}
    </Sheet>
  )
}

function DeleteAccount() {
  const [open, setOpen] = useState(false)
  const [confirm, setConfirm] = useState('')
  const m = useMutation({ mutationFn: () => api('DELETE', '/api/me', { confirm }), onSuccess: () => (location.href = '/entrar') })
  return (
    <Sheet className="mt-5 p-5">
      <h2 className="font-bold">{t('Excluir conta')}</h2>
      <p className="mt-1 text-tinta-2">{t('Remove você de todos os condomínios, apaga suas conversas com o assistente e anonimiza seu nome, telefone e e-mail. Assinaturas já feitas continuam registradas no documento assinado.')}{' '}<a className="underline" href="/privacidade">{t('Saiba mais')}</a>.</p>
      {open ? (
        <form className="mt-3 flex flex-wrap items-end gap-2" onSubmit={(e) => { e.preventDefault(); m.mutate() }}>
          <Field label={t('Digite EXCLUIR para confirmar')} htmlFor="del-c"><Input id="del-c" value={confirm} onChange={(e) => setConfirm(e.target.value)} /></Field>
          <Button type="submit" variant="danger" disabled={confirm !== 'EXCLUIR'} busy={m.isPending}>{t('Excluir minha conta')}</Button>
        </form>
      ) : <Button className="mt-3" variant="danger" onClick={() => setOpen(true)}>{t('Excluir minha conta')}</Button>}
      <div className="mt-2"><ErrorNote error={m.error} /></div>
    </Sheet>
  )
}
